import { derived } from 'svelte/store';
import { devices } from '../../lists/devices';
import { rooms } from '../../lists/rooms';
import { devices_status } from '../../device-runtime/status';
import type { Device } from 'src/types/Device';
import {
	calculateDeviceConsumption,
	createEmptyConsumption,
	mergeConsumptionInto,
	type ConsumptionTotals,
} from './calculate-device-power';

const CONSUMPTION_DEBOUNCE_MS = 400;

type RoomConsumptionMap = Record<string, ConsumptionTotals>;
type DeviceConsumptionMap = Record<string, ConsumptionTotals & { no_room_cons?: boolean }>;

type ConsumptionSnapshot = {
	rooms: RoomConsumptionMap;
	device: DeviceConsumptionMap;
	byAccount: DeviceConsumptionMap;
};

function createDebouncedDerived<T>(
	deps: Parameters<typeof derived>[0],
	compute: (...args: any[]) => T,
	initial: T,
) {
	return derived(
		deps,
		(values, set) => {
			const timeoutId = setTimeout(() => set(compute(...values)), CONSUMPTION_DEBOUNCE_MS);
			return () => clearTimeout(timeoutId);
		},
		initial,
	);
}

function computeAllConsumption(
	_$devices_status: Record<string, unknown>,
	$devices: Record<string, Device>,
	$rooms: Record<string, { id?: string }>,
): ConsumptionSnapshot {
	const rooms_consumption: RoomConsumptionMap = {};
	const device_consumption: DeviceConsumptionMap = {};
	const by_account_consumption: DeviceConsumptionMap = {};

	for (const room in $rooms) {
		rooms_consumption[room] = createEmptyConsumption();
	}

	for (const [deviceKey, device] of Object.entries($devices)) {
		if (typeof device === 'undefined' || typeof deviceKey === 'undefined') continue;

		const status = devices_status.getByRealId(deviceKey);

		if (!device?.shared && device.room_id >= 0) {
			const roomConsumption = calculateDeviceConsumption(
				device,
				deviceKey,
				status,
				$devices,
				'no_account_cons',
				'room',
			);

			if (roomConsumption) {
				if (typeof rooms_consumption[device.room_id] === 'undefined') {
					rooms_consumption[device.room_id] = createEmptyConsumption();
				}

				mergeConsumptionInto(rooms_consumption[device.room_id], roomConsumption);

				if (roomConsumption.roomPowerFrom) {
					rooms_consumption[device.room_id]._power =
						rooms_consumption[device.room_id][roomConsumption.roomPowerFrom];
				}
			}
		}

		const deviceOnlyConsumption = calculateDeviceConsumption(
			device,
			deviceKey,
			status,
			$devices,
			'no_room_cons',
			'device',
		);
		if (deviceOnlyConsumption) {
			device_consumption[deviceKey] = deviceOnlyConsumption;
		}

		const accountConsumption = calculateDeviceConsumption(
			device,
			deviceKey,
			status,
			$devices,
			'no_account_cons',
			'device',
		);
		if (accountConsumption) {
			by_account_consumption[deviceKey] = {
				...accountConsumption,
				no_room_cons: device?.no_room_cons,
			};
		}
	}

	return {
		rooms: rooms_consumption,
		device: device_consumption,
		byAccount: by_account_consumption,
	};
}

const consumptionSnapshot = createDebouncedDerived(
	[devices_status, devices, rooms],
	computeAllConsumption,
	{ rooms: {}, device: {}, byAccount: {} },
);

// Room panel & All rooms view - Realtime energy consumption by room
export const consumption_rooms = derived(consumptionSnapshot, ($snapshot) => $snapshot.rooms);

// Energy panel - Realtime energy consumption by device
export const consumption_device = derived(consumptionSnapshot, ($snapshot) => $snapshot.device);

// Drawer Consumption Panel - Default drawer and Group drawer
export const consumption_by_device_account = derived(
	consumptionSnapshot,
	($snapshot) => $snapshot.byAccount,
);

export type { ConsumptionSnapshot, RoomConsumptionMap, DeviceConsumptionMap };
